import { useState, useEffect } from "react";
import { SupplierServices } from "../services/supplier";
import { MasterDataServices } from "../services/master-data";
import { ISuppliersByNameResponse, ISuppliersByNameResult } from "../interfaces/supplier";
import { MASTERDATA_TYPE } from "../constants/master-data";
import { IMasterDataType } from "../interfaces/master-data";
import { SupplierInvoiceRecordServices } from "../services/supplier-invoice-record";


interface DropdownsDataResult {
  suppliers?: ISuppliersByNameResult[];
  statuses?: string[];
  bookingNos?: string[];
  currencies?: IMasterDataType[];
}

const typeToKeyMap: Record<string, keyof DropdownsDataResult> = {
  statuses: "statuses",
  bookingNos: "bookingNos",
};

async function fetchDropdownByType(
  type: string
): Promise<{ key: keyof DropdownsDataResult; data: any[] }> {
  const res = await SupplierInvoiceRecordServices.getDropdownByType(type);

  return {
    key: typeToKeyMap[type],
    data: res?.data || [],
  };
}

async function fetchCurrencies(): Promise<{ key: keyof DropdownsDataResult; data: any[] }> {
  const res = await MasterDataServices.getAllByType(MASTERDATA_TYPE.CURRENCY);
  return { key: "currencies", data: res?.data || [] };
}

async function fetchSuppliers(): Promise<{ key: keyof DropdownsDataResult; data: any[] }> {
  const res: ISuppliersByNameResponse = await SupplierServices.getSuppliersByName({
    params: { page: 1, pageSize: 5000 },
  });
  return { key: "suppliers", data: res?.data?.results || [] };
}

export const useDropdownsSirData = (enabled = true) => {
  const [data, setData] = useState<DropdownsDataResult>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const reload = () => setReloadCounter((prev) => prev + 1);

  useEffect(() => {
    if (!enabled) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const types = ["statuses"];

        const promises: Promise<{ key: keyof DropdownsDataResult; data: any[] }>[] = [
          ...types.map((type) => fetchDropdownByType(type)),
          fetchCurrencies(),
          fetchSuppliers(),
        ];

        const results = await Promise.all(promises);

        const resultObj = results.reduce((acc, curr) => {
          acc[curr.key] = curr.data;
          return acc;
        }, {} as DropdownsDataResult);

        setData(resultObj);
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reloadCounter, enabled]);

  return { data, loading, error, reload };
};
